import { useEffect, useState, useMemo, useCallback } from "react";
import { Menu, MenuItem, Sidebar, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import RoutingPaths from "helper/routingPath";
import CompanyLogo from "assets/studyGramLogo/studygram_logo.png";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./index.css";

const SidebarComponent = ({ props, callBack }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = width <= 768;

  const menuConfig = useMemo(() => {
    const baseStudentSections = [
      {
        parent_id: 2,
        id: 10,
        title: "Regular",
        path: RoutingPaths.studentsList,
        icon: "pi pi-users me-2",
      },
      {
        parent_id: 2,
        id: 11,
        title: "Demo",
        path: RoutingPaths.demoStudentsList,
        icon: "pi pi-users me-2",
      },
      {
        parent_id: 2,
        id: 12,
        title: "PTE",
        path: RoutingPaths.pteStudentsList,
        icon: "pi pi-users me-2",
      },
      {
        parent_id: 2,
        id: 13,
        title: "Performance",
        path: RoutingPaths.studentPerformanceList,
        icon: "pi pi-file-edit me-2",
      },
      {
        parent_id: 2,
        id: 14,
        title: "Mock Test",
        path: RoutingPaths.mockTestList,
        icon: "pi pi-file-edit me-2",
      },
      {
        parent_id: 2,
        id: 15,
        title: "Fee",
        path: RoutingPaths.feesData,
        icon: "bi bi-cash-coin me-2",
      },
      {
        parent_id: 2,
        id: 19,
        title: "Transaction History",
        path: RoutingPaths.transactionHistory,
        icon: "bi bi-clock-history me-2",
      },
    ];

    const commonSections = [
      {
        id: 1,
        title: "Home",
        path: RoutingPaths.home,
        icon: "pi pi-home me-2",
        subSections: [],
      },
      {
        id: 2,
        title: "Students",
        icon: "pi pi-users",
        subSections: baseStudentSections,
      },
      {
        id: 3,
        title: "Visa",
        path: RoutingPaths.visaList,
        icon: "bi bi-airplane-engines me-2",
        subSections: [],
      },
      {
        id: 4,
        title: "Attendance",
        path: RoutingPaths.attendance,
        icon: "pi pi-calendar me-2",
        subSections: [],
      },
      {
        id: 5,
        title: "Batches",
        path: RoutingPaths.batchesList,
        icon: "pi pi-file me-2",
        subSections: [],
      },
    ];

    const adminOnlySections = [
      {
        id: 6,
        title: "Teachers",
        path: RoutingPaths.teachersList,
        icon: "pi pi-user-edit me-2",
        subSections: [],
      },
      {
        id: 7,
        title: "Expense",
        icon: "fa fa-inr me-2",
        subSections: [
          {
            parent_id: 7,
            id: 17,
            title: "Basic",
            path: RoutingPaths.basicExpensesList,
            icon: "pi pi-euro me-2",
          },
          {
            parent_id: 7,
            id: 18,
            title: "Salary",
            path: RoutingPaths.salaryExpensesList,
            icon: "pi pi-euro me-2",
          },
        ],
      },
      {
        id: 8,
        title: "Others",
        icon: "pi pi-list",
        subSections: [
          {
            parent_id: 8,
            id: 20,
            title: "Users",
            path: RoutingPaths.usersList,
            icon: "pi pi-users me-2",
          },
        ],
      },
      {
        id: 9,
        title: "Settings",
        icon: "pi pi-cog",
        subSections: [
          {
            parent_id: 9,
            id: 16,
            title: "Profile",
            path: RoutingPaths.profile,
            icon: "pi pi-users me-2",
          },
        ],
      },
      {
        id: 20,
        title: "Notes",
        path: RoutingPaths.notesList,
        icon: "pi pi-file-edit me-2",
        subSections: [],
      },
    ];

    return isAdmin ? [...commonSections, ...adminOnlySections] : commonSections;
  }, [isAdmin]);

  const checkUserRole = useCallback(() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("session"));
      setIsAdmin(userInfo?.role === "admin");
    } catch (error) {
      console.error("Error parsing user session:", error);
      setIsAdmin(false);
    }
  }, []);

  const handleWindowSizeChange = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    checkUserRole();
    setIsOpen(props);
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [props, handleWindowSizeChange, checkUserRole]);

  const handleSidebarToggle = useCallback(() => {
    callBack();
  }, [callBack]);

  const handleSidebarClick = useCallback(() => {
    if (isMobile) {
      callBack();
    }
  }, [isMobile, callBack]);

  const renderMenuItem = useCallback(
    (item) => {
      if (item.subSections?.length === 0) {
        const isActive = currentPath === item.path;
        return (
          <MenuItem
            key={item.id}
            component={<Link to={item.path} />}
            className={isActive ? "sidebar_highlight" : ""}
            style={
              isActive
                ? {
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "8px",
                    margin: "0.25rem 0.5rem",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    border: "none",
                    outline: "none",
                  }
                : {}
            }
          >
            <i className={item.icon} aria-hidden="true" />
            {item.title}
          </MenuItem>
        );
      }

      if (item.subSections?.length > 0) {
        return (
          <SubMenu
            key={item.id}
            label={item.title}
            icon={<i className={item.icon} aria-hidden="true" />}
          >
            {item.subSections.map((subItem) => {
              const isActive = currentPath === subItem.path;
              return (
                <MenuItem
                  key={subItem.id}
                  component={<Link to={subItem.path} />}
                  className={isActive ? "sidebar_highlight" : ""}
                  style={
                    isActive
                      ? {
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "8px",
                          margin: "0.25rem 0.5rem",
                          boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                          border: "none",
                          outline: "none",
                        }
                      : {}
                  }
                >
                  <i className={subItem.icon} aria-hidden="true" />
                  {subItem.title}
                </MenuItem>
              );
            })}
          </SubMenu>
        );
      }
      return null;
    },
    [currentPath],
  );

  return (
    <>
      <div
        className={isOpen ? "sidebar__background__blur" : ""}
        onClick={handleSidebarClick}
        role="presentation"
      >
        <aside
          className={
            isOpen ? "card flex position-fixed sidebar__open" : "sidebar__close"
          }
          id="sidebar__style"
          role="navigation"
          aria-label="Main navigation"
          style={{
            background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)",
            color: "#ffffff",
          }}
        >
          <div className="sidebar__header">
            <div className="sidebar__logo-container">
              <img
                src={CompanyLogo}
                alt="StudyGram Logo"
                className="sidebar__company-logo"
              />
            </div>
            <Button
              type="button"
              onClick={handleSidebarToggle}
              icon="pi pi-times"
              className="sidebar__cross_button"
              aria-label="Close sidebar"
            />
          </div>

          <hr className="sidebar__separator" />

          <nav className="sidebar__nav pt-2">
            <h2 className="sidebar__menu-title text-white ms-2 mb-3 mt-3">
              Menu
            </h2>

            <Sidebar>
              <Menu className="menu__sidebar">
                {menuConfig.map((item, index) => (
                  <div key={item.id}>
                    {renderMenuItem(item)}
                    {index < menuConfig.length - 1 && (
                      <hr className="sidebar__separator" />
                    )}
                  </div>
                ))}
              </Menu>
            </Sidebar>
          </nav>
        </aside>
      </div>
    </>
  );
};

export default SidebarComponent;
